import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import type { IJobDescriptionPeran } from '@/types/job-description-peran.types';
import { DataConfig } from '@/utils/hooks/useDefaultDataConfig';
import { FileDown, Loader2 } from 'lucide-react';
import { ORG_DIVISIONS, ORG_LEVELS } from '@/utils/constants/organization-matrix';

interface JobDescriptionPeranTableProps {
  isLoading: boolean;
  data: {
    content: IJobDescriptionPeran[];
    totalElements: number;
    totalPages: number;
  };
  pageConfig: DataConfig;
  setPageConfig: React.Dispatch<React.SetStateAction<DataConfig>>;
  onDetail: (row: IJobDescriptionPeran) => void;
  onDownload: (row: IJobDescriptionPeran) => void;
  /** Id jabatan yang dokumennya sedang diunduh; semua tombol download disabled, baris itu menampilkan spinner. */
  downloadingId?: IJobDescriptionPeran['id'] | null;
  mode?: 'detail' | 'download';
}

function getDivisionLabel(key?: string): string {
  const found = ORG_DIVISIONS.find(d => d.key === key);
  return found?.label ?? key ?? '—';
}

function getLevelLabel(key?: string): string {
  const found = ORG_LEVELS.find(l => l.key === key);
  return found?.label ?? key ?? '—';
}

export default function JobDescriptionPeranTable({
  isLoading,
  data,
  pageConfig,
  setPageConfig,
  onDetail,
  onDownload,
  downloadingId = null,
  mode = 'detail',
}: JobDescriptionPeranTableProps) {
  const columns: MainTableColumn[] = [
    {
      name: 'name',
      label: 'Nama Jabatan',
      sortKey: 'org_level',
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <span className="font-semibold text-gray-800 dark:text-gray-100">{row.name}</span>
      ),
    },
    {
      name: 'division_key',
      label: 'Departemen',
      sortable: false,
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <span className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
          {getDivisionLabel(row.division_key)}
        </span>
      ),
    },
    {
      name: 'level_key',
      label: 'Level',
      sortable: false,
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">{getLevelLabel(row.level_key)}</span>
      ),
    },
    {
      name: 'job_description_count',
      label: 'Jumlah Job Desc',
      sortKey: 'job_description_count',
      headerClassName: 'text-center',
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <div className="flex justify-center">
          <span
            className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ${
              row.job_description_count > 0
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            {row.job_description_count} Uraian
          </span>
        </div>
      ),
    },
    {
      name: 'aksi',
      label: 'Aksi',
      sortable: false,
      headerClassName: 'text-center',
      render: (_: unknown, row: IJobDescriptionPeran) => (
        <div className="flex justify-center items-center">
          {mode === 'download' ? (
            <button
              type="button"
              aria-label={`Download PDF ${row.name}`}
              className="text-gray-500 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
              disabled={downloadingId !== null}
              onClick={() => onDownload(row)}
            >
              {downloadingId === row.id ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
            </button>
          ) : (
            <button
              type="button"
              aria-label={`Kelola ${row.name}`}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              onClick={() => onDetail(row)}
            >
              Kelola
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <MainTable
      showHeader={false}
      isLoading={isLoading}
      data={{
        content: data.content as unknown as { [key: string]: unknown }[],
        totalElements: data.totalElements,
        totalPages: data.totalPages,
      }}
      columns={columns}
      pageConfig={pageConfig}
      setPageConfig={setPageConfig}
      showActions={false}
      sectionClassName="px-5"
    />
  );
}
